/**
 * ID Generator Utility - Frontend
 * 
 * Matches backend ID generation logic to ensure consistency.
 * Ensures all IDs are properly sanitized (trimmed, uppercased).
 */

/**
 * Sanitize an existing assessment ID by trimming whitespace
 * @param {string} id - ID to sanitize
 * @returns {string} - Sanitized ID
 */
export const sanitizeAssessmentId = (id) => {
  if (!id || typeof id !== 'string') {
    return ''
  }
  
  return id.trim()
}

/**
 * Convert a string to a URL-friendly slug
 * Removes accents, converts to lowercase, replaces spaces with hyphens
 * @param {string} text - Text to slugify
 * @returns {string} - Slugified text
 * 
 * @example
 * slugify('Stratégie et Vision') // Returns: 'strategie-et-vision'
 */
export const slugify = (text) => {
  if (!text || typeof text !== 'string') {
    return ''
  }
  
  return text
    .normalize('NFD') // Decompose accents
    .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

/**
 * Generate a question ID based on theme and question indices
 * @param {number} themeIndex - Theme index (0-based)
 * @param {number} questionIndex - Question index (0-based)
 * @returns {string} - Question ID in format: q{theme+1}_{question+1}
 * 
 * @example
 * generateQuestionId(0, 0) // Returns: 'q1_1'
 * generateQuestionId(2, 4) // Returns: 'q3_5'
 */
export const generateQuestionId = (themeIndex, questionIndex) => {
  return `q${themeIndex + 1}_${questionIndex + 1}`
}

/**
 * Generate a unique assessment ID
 * @param {Object} clientInfo - Client information object
 * @param {string} clientInfo.lastName - Client's last name (will be sanitized)
 * @param {string} [reportId] - Optional pre-generated report ID
 * @returns {string} - Sanitized assessment ID in format: CEDEC-{timestamp}-{LASTNAME}
 * 
 * @example
 * generateAssessmentId({ lastName: 'Dupont ' }) 
 * // Returns: 'CEDEC-1234567890-DUPONT'
 */
export const generateAssessmentId = (clientInfo, reportId = null) => {
  // If reportId is provided, sanitize it before returning
  if (reportId) {
    return sanitizeAssessmentId(reportId)
  }
  
  // Sanitize lastName: trim whitespace, uppercase, handle null/undefined
  const sanitizedLastName = (clientInfo?.lastName || 'UNKNOWN')
    .trim()
    .toUpperCase()
  
  // Generate ID with current timestamp
  return `CEDEC-${Date.now()}-${sanitizedLastName}`
}

export default {
  generateAssessmentId,
  sanitizeAssessmentId,
  slugify,
  generateQuestionId
}
